import React from "react";
import {
  Grid, Container, Box, Typography, Divider, CircularProgress,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AppWidgetSummary from "../sections/app/AppWidgetSummary";
import {
  fetchPendingConsmer,
  fetchNewConsumer,
  manageSingleAadhaar,
  manageDoubleAadhaar,
  fetchDashBoard,
  fetchAllCustomer,
  svreadylistFun,
} from "Service/agent.service";
import DataGrid from "components/table/DataGrid";
import { Helmet } from "react-helmet-async";
import { getUserName } from "Service/Service";
import { PRIMARY } from "themes/colors";

// ----------------------------------------------------------------------

const widgetAnimation = {
  transition: "transform 0.25s ease, box-shadow 0.25s ease",
  cursor: "pointer",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
  },
};

const AgentDashBoard = () => {
  const agentName = getUserName();

  const [customerTotal, setCustomerTotal] = React.useState({
    newCustomer: 0,
    oldCustomer: 0,
    totalConnection: 0,
    installationComplete: 0,
    totalLoad: 0,
  });
  const [loading, setLoading] = React.useState(false);
  const [tableData, setTableData] = React.useState<any[]>([]);
  const [tableTitle, setTableTitle] = React.useState("");
  const [singleUser, setSingleUser] = React.useState(0);
  const [doubleUser, setDoubleUser] = React.useState(0);
  const [svReadyCount, setSvReadyCount] = React.useState(0);

  React.useEffect(() => {
    fetchInitialData();
  }, []);

  const columns = [
    { field: "name", title: "Name" },
    { field: "mainAadhaar", title: "Main Aadhaar" },
    { field: "familyAadhaar", title: "Family Aadhaar" },
    { field: "mobile", title: "Mobile" },
    { field: "consumerNo", title: "Consumer No" },
    { field: "registeredAgencyName", title: "Registered Agency" },
    { field: "subAgent", title: "Sub Agent" },
    { field: "installtatus", title: "Installation Status" },
    { field: "remarks", title: "Remarks" },
    { field: "registerDate", title: "Register Date" },
  ];

  const fetchData = async (fetcher: () => Promise<any>, title: string) => {
    try {
      setLoading(true);
      const result = await fetcher();
      setTableData(result);
      setTableTitle(title);
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [dashboardData, newConsumerData, svReadyData] = await Promise.all([
        fetchDashBoard(),
        fetchNewConsumer(),
        svreadylistFun(),
      ]);
      if (dashboardData) setCustomerTotal(dashboardData);
      setSingleUser(newConsumerData.filter((d: any) => d.isSingleWomen === true).length);
      setDoubleUser(newConsumerData.filter((d: any) => d.isSingleWomen === false).length);
      setSvReadyCount(svReadyData.length);
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const actionCards = [
    {
      title: "SV Ready",
      total: svReadyCount,
      icon: "mdi:user-check",
      color: "success",
      onClick: () => fetchData(svreadylistFun, "SV Ready List"),
    },
    {
      title: "PMUY 2.0 Registration",
      total: customerTotal?.newCustomer,
      icon: "mdi:account-plus",
      color: "info",
      onClick: () => fetchData(fetchAllCustomer, "PMUY 2.0 Registration List"),
    },
    {
      title: "PMUY 2.0 Consumer",
      total: customerTotal?.totalConnection,
      icon: "mdi:account-multiple",
      color: "warning",
      onClick: () => fetchData(fetchNewConsumer, "PMUY 2.0 Consumer List"),
    },
    {
      title: "Single Aadhaar",
      total: singleUser,
      icon: "mdi:account",
      color: "primary",
      onClick: () => fetchData(manageSingleAadhaar, "Single Aadhaar Consumer List"),
    },
    {
      title: "Double Aadhaar",
      total: doubleUser,
      icon: "mdi:account-multiple-outline",
      color: "error",
      onClick: () => fetchData(manageDoubleAadhaar, "Double Aadhaar Consumer List"),
    },
    {
      title: "Today's Registration",
      total: customerTotal?.totalLoad - customerTotal?.installationComplete,
      icon: "mdi:calendar-today",
      color: "secondary",
      onClick: () => fetchData(fetchPendingConsmer, "Today's Registration List"),
    },
    {
      title: "Installation Pending",
      total: customerTotal?.totalLoad - customerTotal?.installationComplete,
      icon: "mdi:progress-wrench",
      color: "error",
      onClick: () => fetchData(fetchPendingConsmer, "Installation Pending List"),
    },
  ];

  const summaryCards = [
    {
      title: "Total Load",
      total: customerTotal?.totalLoad,
      icon: "mdi:upload",
      color: "info",
    },
    {
      title: "Installation Complete",
      total: customerTotal?.installationComplete,
      icon: "mdi:check-circle",
      color: "success",
    },
    {
      title: "Total Old Consumer",
      total: customerTotal?.oldCustomer,
      icon: "mdi:account-convert",
      color: "warning",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Agent Dashboard | Jaman HP Gas</title>
      </Helmet>

      {/* Page Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${PRIMARY.darker} 0%, ${PRIMARY.dark} 50%, ${PRIMARY.main} 100%)`,
          color: "white",
          px: { xs: 2.5, sm: 4 },
          py: { xs: 3, sm: 4 },
          mb: 0,
        }}
      >
        <Typography variant="h5" fontWeight={700} lineHeight={1.2}>
          Agent Dashboard
        </Typography>
        {agentName && (
          <Typography variant="body2" sx={{ opacity: 0.78, mt: 0.5 }}>
            Welcome back, {agentName}
          </Typography>
        )}
      </Box>

      <Container maxWidth="xl" sx={{ py: { xs: 3, sm: 4 } }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
            <CircularProgress size={48} thickness={4} />
          </Box>
        ) : (
          <>
            {/* Action Cards — click to load table */}
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", fontSize: "0.7rem" }}
              >
                Click a card to view list
              </Typography>
            </Box>
            <Grid container spacing={2.5} sx={{ mb: 4 }}>
              {actionCards.map((card, i) => (
                <Grid item xs={6} sm={4} md={3} lg={12 / 7} key={i}>
                  <AppWidgetSummary
                    title={card.title}
                    total={card.total}
                    icon={card.icon}
                    color={card.color as any}
                    animation={widgetAnimation}
                    onClick={card.onClick}
                  />
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ mb: 3, borderStyle: "dashed" }} />

            {/* Summary Cards */}
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", fontSize: "0.7rem" }}
              >
                Overall Summary
              </Typography>
            </Box>
            <Grid container spacing={2.5}>
              {summaryCards.map((card, i) => (
                <Grid item xs={12} sm={4} key={i}>
                  <AppWidgetSummary
                    title={card.title}
                    total={card.total}
                    icon={card.icon}
                    color={card.color as any}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Data Table */}
            {tableData.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Divider sx={{ mb: 3, borderStyle: "dashed" }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", fontSize: "0.7rem", display: "block", mb: 1.5 }}
                >
                  {tableTitle}
                </Typography>
                <DataGrid title={tableTitle} rows={tableData} columns={columns} />
              </Box>
            )}
          </>
        )}
      </Container>
    </>
  );
};

export default AgentDashBoard;
